import React from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { Box } from '@mui/material';
import { Typography } from '@mui/material';
import { Popover } from '@mui/material';
import { Button, ToggleButton } from '@repo/ui';
import { Check, Rows3, LayoutGrid } from 'lucide-react';
import { DEFAULT_LAYOUT, DISPLAYED_DATA_DEFAULTS } from 'constants/certificateConstants';
import { border } from 'settings/tokens';
import { FIELD_RADIUS_MAX } from 'components/UI/fieldMetrics';
import { SectionLabel } from 'components/UI/SectionLabel';
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter';

export interface DisplayedDataPopoverProps {
  uiState: any;
  displayedData: Record<string, boolean>;
  layout: 'list' | 'grid';
  onLayoutChange: (next: 'list' | 'grid') => void;
  displayedDataAnchor: HTMLElement | null;
  handleDisplayedDataChange: (key: string, checked: boolean) => void;
  setOpenDisplayedData: (open: boolean) => void;
  setDisplayedDataAnchor: (anchor: HTMLElement | null) => void;
}

const displayedDataItems: { key: string; label: string; locked?: boolean }[] = [
  // Product name is mandatory (always shown) — locked on, non-interactive.
  { key: 'product_name', label: 'Product name', locked: true },
  { key: 'certificate_id', label: 'Certificate ID' },
  /*
    "Actions", not "Menu item". Every other row in this list names the THING it
    shows on the card — Image, Title, Status — and this one named its own
    implementation instead. `menu_item` is the storage key (unchanged, it is
    persisted in localStorage), but the label a user reads should say what
    toggling it does: it shows or hides the card's actions.
  */
  { key: 'menu_item', label: 'Actions' },
  { key: 'last_activity', label: 'Last activity' },
];

export const DisplayedDataPopover = ({
  uiState,
  displayedData,
  layout,
  onLayoutChange,
  displayedDataAnchor,
  handleDisplayedDataChange,
  setOpenDisplayedData,
  setDisplayedDataAnchor,
}: DisplayedDataPopoverProps) => {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));

  /*
   * ONE COMMIT MODEL: both branches apply as you go.
   *
   * This file used to argue the opposite, and the argument is recorded here
   * because it was a reasonable one that lost. It ran: the desktop popover is
   * small and anchored beside the list, so live-apply gives you a real preview;
   * the mobile sheet is a bottom drawer at up to 85vh that COVERS the list it
   * would be previewing, so live-apply buys nothing you can see while costing the
   * one thing a sheet with buttons promises — that Cancel means cancel.
   *
   * What it did not weigh is the cost of the split itself. Two commit models for
   * one control means the same tap does different things at 374px and 376px, and
   * the divergence had already produced a defect: mobile's `handleReset` cleared
   * only the COLUMN rows while its `dirty` check counted the layout toggle, so
   * pressing Reset with Grid selected left the sheet still dirty and the toggle
   * untouched — a Reset visibly lying about its own scope, two rows above the
   * thing it did not reset. The desktop reset had always covered both.
   *
   * The user's call is auto-apply everywhere (filter, sort and layout, at both
   * breakpoints), so the mobile path collapses onto the desktop one: no draft, no
   * `dirty`, no Apply, one reset that covers the toggle AND the rows. The scope
   * bug goes with the code that had it rather than being patched in place.
   *
   * No debounce here, unlike the filter and sort sheets. Nothing this writes is a
   * QUERY — it is localStorage plus a re-render of already-loaded cards — so there
   * is no request to coalesce, and `handleReset` below already skips writes for
   * values that already match.
   */
  /*
   * Reset writes the defaults straight through the same
   * `handleDisplayedDataChange` / `onLayoutChange` the rows themselves use — the
   * platform's model too (`LayoutOptions`'s Reset calls `onResetColumns()` against
   * a live grid, with nothing staged anywhere).
   *
   * It resets BOTH sections: the list/grid toggle AND the column rows. That is the
   * fix for the mobile scope bug described above, and it is what the platform's
   * Reset does across every implemented control in its Layout menu.
   *
   * Only what actually differs is written, so a Reset does not rewrite every
   * localStorage row for values that already match — which is also why leaving the
   * control enabled at defaults is harmless: it writes nothing.
   */
  const handleReset = () => {
    displayedDataItems.forEach(item => {
      if (item.locked) return;
      const next = Boolean(
        DISPLAYED_DATA_DEFAULTS[item.key as keyof typeof DISPLAYED_DATA_DEFAULTS],
      );
      if (Boolean(displayedData[item.key]) !== next) handleDisplayedDataChange(item.key, next);
    });
    if (layout !== DEFAULT_LAYOUT) onLayoutChange(DEFAULT_LAYOUT);
  };

  return (
    <>
      {uiState.openDisplayedData &&
        (isMobile ? (
          <DrawerPopup
            title='Display'
            fullScreen={false}
            /* NO 'Cancel' — the header X that DrawerPopup draws by default. Every
               row here applies on tap, so nothing is pending for a Cancel to
               discard and the word would promise a revert to the layout you
               arrived with that this sheet cannot perform. Reset goes to the
               DEFAULTS, which is a different destination and says so. */
            // Parity, measured rather than eyeballed. The two gaps were 13px
            // (divider -> first row box) and 32px (last row box -> buttons) — the
            // BOTTOM was the loose one, not the top. Measuring to the text boxes
            // hid that, because line-height leading pads a text box well past the
            // glyphs; only the row containers show the real edges.
            contentSx={{ pt: '16px' }}
            child={
              <Stack sx={{ gap: '8px' }}>
                <LayoutPicker layout={layout} onLayoutChange={onLayoutChange} />
                <DisplayedDataOptions
                  displayedData={displayedData}
                  handleDisplayedDataChange={handleDisplayedDataChange}
                />
                {/*
                  The SAME footer as the filter and sort sheets, in the same
                  reset-only mode. `count` is omitted because a count of displays is
                  meaningless — this sheet is never "empty", it holds a layout and
                  some columns.
                */}
                <FilterSheetFooter onReset={handleReset} />
              </Stack>
            }
            handleOpen={uiState.openDisplayedData}
            handleClose={() => setOpenDisplayedData(false)}
          />
        ) : (
          <Popover
            open={uiState.openDisplayedData}
            anchorEl={displayedDataAnchor}
            onClose={() => {
              setOpenDisplayedData(false);
              setDisplayedDataAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              /*
               * 16px of gutter, matched to the platform's equivalent surface —
               * the data-table Layout menu
               * (`apps/platform/src/components/input/LayoutOptions/LayoutOptions.tsx`,
               * opened from `ActionLine`).
               *
               * MEASURED rather than copied, because the two apps disagree on the
               * spacing base (§29): the platform's `p: 2` is 8px on @repo/ui's 4px
               * base, and this file's old `p: 1` was 8px on MUI's default 8px base,
               * so the two LOOKED like different factors while producing the same
               * number. The number was never the point — what makes the platform
               * menu read roomy is that its rows carry 12px of their OWN padding on
               * top of the paper's 8, so a row label sits ~24px in from the paper
               * edge. Here the rows carry 8, so at a 8px paper the label sat 16px
               * in and the section caption sat 8px in, hard against the corner
               * radius. Measured on the platform: paper `0px 8px 8px`, rows 12px,
               * label ~24px from the edge. Measured here before: paper 8px, rows
               * 8px, label 16px from the edge, caption 8px.
               *
               * 16 here puts the row label at 16 + 8 = 24 — the platform's inset —
               * and gives the caption a real top margin instead of the 8px it had.
               * Absolute px, not a factor, so the value cannot be misread as the
               * other app's base.
               *
               * minWidth rises 280 -> 296 by the same 16 so the extra gutter is
               * added AROUND the content rather than taken out of it: the layout
               * toggle stays exactly as wide as it was, and the outer width lands
               * on the platform menu's measured 298.
               */
              sx: { p: '16px', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 296 },
            }}
          >
            <>
              <LayoutPicker layout={layout} onLayoutChange={onLayoutChange} />
              <DisplayedDataOptions displayedData={displayedData} handleDisplayedDataChange={handleDisplayedDataChange} />
              {/*
                RESET ONLY — no Apply, and that is `FilterSheetFooter`'s documented
                second mode rather than an omission: "OMIT it to get the AUTO-APPLY
                footer — reset only, no Apply button", which is precisely this
                popover's commit model and the platform filter drawer's.

                `mode='panel'` for the same reason the platform's is a panel
                affordance: this is pointer-driven, so 40px right-aligned controls,
                not the 44px thumb targets the phone sheet below uses.

                ALWAYS RENDERED, and always ENABLED. It was gated on an
                `at-defaults` check mirroring `LayoutOptions`'s `isLayoutModified` — the
                platform hides its Reset link rather than showing a control that
                would do nothing. That gate was wrong HERE, and auto-apply is what
                made it wrong: with every row applying on click, the very first
                click flips `atDefaults` false and the footer materialises, so the
                popover changes height under the cursor on the first interaction,
                every time. A control that pops into existence as you use the menu
                is worse than a control that occasionally does nothing.
                (Enabled rather than disabled-at-defaults because §46 requires a
                disabled control to carry a readable reason, and "nothing to reset"
                does not earn a tooltip — whereas `handleReset` skips every write
                whose value already matches, so a click at defaults costs nothing.)

                Same in both branches now: the mobile sheet's footer was always
                present, so this closes a desktop-only asymmetry rather than
                introducing one.
              */}
              <FilterSheetFooter
                mode='panel'
                /*
                  FULL WIDTH. `panel` right-aligns at `minWidth: 120`, which is
                  right for the platform's 500px filter drawer — a pair of
                  controls at the end of a wide row. This popover is ~296px and
                  Reset is ALONE in it, so right-alignment has nothing to align
                  against and the empty left half reads as a missing button.
                */
                fullWidthReset
                onReset={handleReset}
                // 16px, matching the paper's own `p: '16px'` gutter rather than
                // the 24 the phone sheet pays — the popover is a denser surface
                // and its rows already sit tighter than the sheet's.
                sx={{ paddingTop: '16px' }}
              />
            </>
          </Popover>
        ))}
    </>
  );
};

/**
 * List vs grid.
 *
 * Lives in Display rather than as a fifth toolbar icon: it changes how the same
 * data is PRESENTED, which is exactly what the other rows in this menu do, and
 * the toolbar is already at four controls on a 343px row.
 *
 * The shared `@repo/ui` ToggleButton, not a hand-rolled segmented control. The
 * previous version was two tappable Stacks that swapped a background colour
 * instantly — correct in structure but with no motion, so the selection appeared
 * to teleport between halves. ToggleButton animates the selected pill in BOTH
 * directions (the transition sits on every tab, not on `.Mui-selected`, so the
 * outgoing half animates too) and is the same control the platform uses for its
 * billing period, which is where this pattern already lives.
 *
 * Exclusive by construction — a tab list cannot have two selected or none, which
 * is the thing two check-rows would get wrong.
 */
const LayoutPicker = ({
  layout,
  onLayoutChange,
}: {
  layout: 'list' | 'grid';
  onLayoutChange: (next: 'list' | 'grid') => void;
}) => (
  <Stack sx={{ mb: '4px' }}>
    {/* `tone='menu'` — the quieter caption register. See SectionLabel's `tone`
        doc for why a popover caption and a page caption differ on purpose. */}
    <SectionLabel first tone='menu'>
      Layout
    </SectionLabel>
    <ToggleButton
      fullWidth
      tab={layout}
      onChange={value => onLayoutChange(value as 'list' | 'grid')}
      /* Grid FIRST, because it is the default (`DEFAULT_LAYOUT`). A two-option
         toggle reads left-to-right as "the usual one, then the alternative", so
         a default sitting on the right says the opposite of what it is. */
      tabs={[
        { label: 'Grid', value: 'grid', icon: <LayoutGrid size={18} /> },
        { label: 'List', value: 'list', icon: <Rows3 size={18} /> },
      ]}
      // Full width of the sheet, halves equal — the control is the row here, not
      // a chip sitting in it.
      /*
        The shared control, wearing its OWN chrome.

        This used to override the radius, the pill's nested radius, the label weight
        and the shadow, on the argument that the default pill read as a foreign object
        in a sheet full of fields. Reverted: the platform's Studio alignment toggle
        (LogoUploadCard) renders the same `@repo/ui` ToggleButton with none of that,
        and the two now look like one control instead of two takes on one.

        What the default buys, and what the overrides were quietly flattening: the
        pill's transition is what makes the selection appear to MOVE between halves
        rather than blink.

        The rest of that argument — that a heavy shadow and a hard white fill are
        what say the selected half is "on top" — no longer applies, and the shared
        component has since dropped both (2026-08-22). A hardcoded white pill does
        not follow the theme, so in dark mode it rendered as a glaring white chip;
        it is now `background.paper` + `text.primary` + weight 600, matching the
        platform's `SegmentedTabs`. So do NOT reintroduce local overrides to get
        the raised look back — that would recreate the exact "two takes on one
        control" this comment was written to prevent, and the fix belongs in
        `@repo/ui` for both apps (working-conventions §47).
      */
    />
    {/* Belongs to the Layout stack, not the options list — it labels what FOLLOWS,
        and the options component renders only rows. */}
    <SectionLabel tone='menu'>Columns</SectionLabel>
  </Stack>
);

const DisplayedDataOptions = ({
  displayedData,
  handleDisplayedDataChange,
}: {
  displayedData: Record<string, boolean>;
  handleDisplayedDataChange: (key: string, checked: boolean) => void;
}) => {
  return (
    <Stack spacing={0.5}>
      {displayedDataItems.map(item => {
        const locked = !!item.locked;
        const checked = locked ? true : !!displayedData[item.key as keyof typeof displayedData];
        const toggle = () => {
          if (locked) return;
          handleDisplayedDataChange(item.key, !checked);
        };
        return (
          <Stack
            key={item.key}
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            onClick={toggle}
            /*
             * Focusable, and announced as a toggle.
             *
             * This was a bare `div` with an `onClick` — so a keyboard user could
             * neither reach a row nor operate one, and a `:focus-visible` rule
             * would have been dead CSS. The hover treatment below is the row's
             * only affordance, so it has to be reachable by focus for it to mean
             * anything; adding the styling without the focusability would look
             * like the affordance existed while it did not.
             *
             * A locked row is not interactive at all, so it stays out of the tab
             * order rather than becoming a focus stop that does nothing.
             */
            role={locked ? undefined : 'checkbox'}
            aria-checked={locked ? undefined : checked}
            tabIndex={locked ? undefined : 0}
            onKeyDown={
              locked
                ? undefined
                : event => {
                    // Space is the checkbox key; Enter is accepted too because a
                    // row that looks clickable should answer the obvious key.
                    if (event.key === ' ' || event.key === 'Enter') {
                      event.preventDefault();
                      toggle();
                    }
                  }
            }
            sx={{
              // Tightened: 52px rows with 14px of vertical padding gave the
              // drawer more air than the list it configures. 44 is still the tap
              // minimum.
              px: 1,
              py: 1,
              minHeight: 44,
              borderRadius: '12px',
              cursor: locked ? 'default' : 'pointer',
              opacity: locked ? 0.45 : 1,
              backgroundColor: 'transparent',
              /*
               * Hover paints the row's TEXT in the brand colour, on top of the
               * existing grey wash — the wash says "this row", the colour says
               * "this row is a control".
               *
               * `brand[600]` is the tenant's own `primary_color` unmixed
               * (`settings/mui/brandRamp.ts`), which is what makes this the
               * BRAND's accent rather than Vera's; `primary.main` is the
               * documented fallback, and is the same idiom as
               * `components/Fields/SelectField.tsx`. On a tenant that has set no
               * colour BOTH resolve to black, so the hover is legitimately
               * invisible there — that is the neutral treatment an unthemed
               * tenant is supposed to get, not a bug to patch with a hardcoded
               * hue.
               *
               * Focus gets the identical treatment: with the row now focusable,
               * the keyboard path and the pointer path show the same thing.
               */
              '&:hover, &:focus-visible': locked
                ? undefined
                : {
                    backgroundColor: (theme: any) => theme.palette.grey[50],
                    // Set on the ROW, not on the label, so the trailing check —
                    // which draws in `currentColor` — travels with it. Painting
                    // only the Typography left a black tick beside a brand-
                    // coloured label.
                    color: (theme: any) =>
                      theme.palette.brand?.[600] || theme.palette.primary.main,
                    '& .MuiTypography-root': { color: 'inherit' },
                  },
              '&:focus-visible': locked
                ? undefined
                : {
                    outline: (theme: any) =>
                      `2px solid ${theme.palette.brand?.[600] || theme.palette.primary.main}`,
                    outlineOffset: '-2px',
                  },
            }}
          >
            {/*
              Selected rows keep full contrast; unselected ones drop to
              `text.secondary`.

              Before this the two were identical black and the trailing check was
              the ONLY difference, so reading which columns were on meant
              scanning a narrow icon column rather than the labels themselves.
              Colour carries it at a glance and the check stays as the explicit
              confirmation.

              Weight is deliberately NOT used to draw the distinction: these are
              16px rows, and a weight step lays down noticeably more ink at 16
              than the 14px contexts most of this app's weight rules were written
              for. Colour alone is the lighter instrument, which is the whole
              request.

              A locked row reads as selected — it IS permanently on — and its
              existing 0.45 opacity is what says it cannot be changed.
            */}
            <Typography sx={{ fontSize: 16, color: checked ? 'text.primary' : 'text.secondary' }}>
              {item.label}
            </Typography>
            {/* `currentColor`, so the check follows the row — including into the
                brand colour on hover. It was a hardcoded #000000, which stayed
                black while the row it belongs to changed, and which ignored a
                tenant's own `text_color` at rest. */}
            {checked && <Check size={20} color='currentColor' strokeWidth={2} />}
          </Stack>
        );
      })}
    </Stack>
  );
};
